import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';

import {CreateAiContentItemRequest} from 'common/src/modules/aiContent/aiContentItem.types';
import {AiContentVariationColumnDataType} from 'common/src/modules/aiContent/aiContentProject.types';
import createMultipleAiContentItems from 'common/src/modules/aiContent/store/aiContentProject/thunks/createMultipleAiContentItems';
import assetsApi from 'common/src/modules/Assets/Assets.api';
import {AssetFolderEntityType} from 'common/src/modules/Assets/Assets.enums';
import getAllAssetsFromTree from 'common/src/modules/Assets/utils/getAllAssetsFromTree';
import {assetDisplayConfigs} from 'common/src/modules/Assets/store/assetDisplayConfigs';
import {loadAssetTree} from 'common/src/modules/Assets/store/shared/thunks/loadAssetTree';
import {AiContentProjectAgentToolName} from '../aiContentProjectAgent.enums';

const inputSchema = z.object({
  items: z
    .array(
      z.object({
        name: z.string().describe('The name of the item to create'),
        inputValues: z
          .record(z.string())
          .optional()
          .nullable()
          .describe('Key-value pairs of input column values for this item. for image/videos the values must be asset Ids, as string'),
      }),
    )
    .describe('Array of items to create with their input values'),
});

/**
 * Collect asset IDs from image/video input columns and link any that are not
 * already present in the project's asset tree.
 */
async function linkMissingAssetsToProject(store: CLStore, items: {inputValues?: Record<string, string> | null}[]) {
  const state = store.getState();
  const project = state.aiContentProjectPage?.project;
  const projectAssets = state.aiContentProjectPage?.projectAssets;

  if (!project || !projectAssets?.assetTree) return;

  const columns = project.variationInputSchema?.columns ?? [];
  const imageVideoColumnNames = new Set(
    columns
      .filter(
        (column) =>
          column.dataType === AiContentVariationColumnDataType.Image ||
          column.dataType === AiContentVariationColumnDataType.Video,
      )
      .map((column) => column.name)
      .filter(Boolean) as string[],
  );

  if (imageVideoColumnNames.size === 0) return;

  // Collect all unique asset IDs from input values
  const assetIds = new Set<number>();

  items.forEach((item) => {
    if (!item.inputValues) return;

    imageVideoColumnNames.forEach((columnName) => {
      const value = item.inputValues?.[columnName];

      if (value !== null && value !== undefined) {
        const assetId = Number(value);

        if (!isNaN(assetId) && assetId > 0) {
          assetIds.add(assetId);
        }
      }
    });
  });

  if (assetIds.size === 0) return;

  // Check which assets are already in the project tree
  const existingAssetIds = new Set(
    getAllAssetsFromTree(projectAssets.assetTree).map((asset) => asset.id),
  );

  const missingAssetIds = Array.from(assetIds).filter(
    (assetId) => !existingAssetIds.has(assetId),
  );

  if (missingAssetIds.length === 0) return;

  // Link missing assets to the project's root folder via API
  const rootFolderId = projectAssets.assetTree.folderId;

  await Promise.all(
    missingAssetIds.map((assetId) =>
      assetsApi.linkAsset(assetId, {
        entityType: AssetFolderEntityType.AiContentProject,
        folderId: rootFolderId,
      }),
    ),
  );

  // Reload the asset tree to update the Redux slice
  const config = assetDisplayConfigs[AssetFolderEntityType.AiContentProject];

  await store.dispatch(
    loadAssetTree({
      entityType: config.entityType,
      selectEntityId: config.selectEntityId,
      depth: config.loadAssetTreeDepth,
      setAssetTree: config.setAssetTree,
      setCurrentFolderId: config.setCurrentFolderId,
      setIsLoadingTree: config.setIsLoadingTree,
    }),
  ).unwrap();
}

export default function createItemsTool(store: CLStore) {
  async function execute({
    input: {items},
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const state = store.getState();
    const project = state.aiContentProjectPage?.project;

    if (!project?.id) {
      return toolError('No project is currently loaded. Cannot create items.');
    }

    if (!items || items.length === 0) {
      return toolError('No items provided. Please specify at least one item to create.');
    }

    const projectId = project.id;

    const itemRequests: CreateAiContentItemRequest[] = items.map((item) => ({
      name: item.name,
      projectId,
      inputValues: item.inputValues ?? {},
    }));

    try {
      // Link any image/video assets to the project before creating items
      await linkMissingAssetsToProject(store, items);

      await store
        .dispatch(
          createMultipleAiContentItems({
            projectId,
            request: {items: itemRequests},
          }),
        )
        .unwrap();

      const itemNames = items.map((item) => item.name).join(', ');

      return toolResult(
        `Successfully created ${items.length} item${items.length > 1 ? 's' : ''}: ${itemNames}`,
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);

      return toolError(`Error creating items: ${errorMessage}`);
    }
  }

  return tool({
    displayName: 'Creating items',
    name: AiContentProjectAgentToolName.CreateItems,
    description:
      'Creates one or more AI content items with specified input values. Each item represents a variation that will have prompts and assets generated. Use this tool even when creating a single item.',
    execute,
    inputSchema,
  });
}
